import { boundedPromise } from "#bounded-promise";
import { DeadlineExceededError } from "#deadline-exceeded-error";
import { type Deadline, resolveDeadlineMs } from "#deadline-ms";

/** Every run and every wait on a poisoned queue fails with this, at once. */
export class SerialQueuePoisonedError extends Error {
  constructor(queue: string, cause: unknown) {
    super(`${queue} is unusable: an earlier operation never finished`, { cause });
    this.name = "SerialQueuePoisonedError";
  }
}

/**
 * What the queue does when the RUNNING task outlives its run deadline (its awaiter has already
 * been rejected with `DeadlineExceededError`):
 *
 *   * `advance` — start the next task now. For independent or idempotent tasks (a slide
 *     completion, a notification) whose overlap with a straggler is harmless.
 *   * `poison` — the straggler may still be using the guarded resource (a native connection that
 *     must never interleave), so nothing else may start on it: every queued and future task fails
 *     immediately with `SerialQueuePoisonedError`, and `onPoisoned` runs so the owner can replace
 *     the resource (close the connection, open a new generation).
 *
 * There is no third option. "Keep the place until the work settles" is exactly the policy that
 * turns one call that never answers into a resource that hangs every caller until restart.
 */
export type SerialQueueOverrunPolicy = "advance" | "poison";

export type BoundedSerialQueue = {
  /** Whether a task is running or waiting. */
  readonly idle: boolean;
  /** Fail every waiter and every future run with `SerialQueuePoisonedError`. */
  poison(cause: unknown): void;
  readonly poisoned: boolean;
  /**
   * Run `task` after every earlier task. Rejects with `DeadlineExceededError` if it waits longer
   * than `waitDeadline` for its turn (it is then skipped, never run) or runs longer than
   * `runDeadline`.
   */
  run<T>(
    operation: string,
    task: (signal: AbortSignal) => Promise<T>,
    options?: { runDeadline?: Deadline; waitDeadline?: Deadline },
  ): Promise<T>;
};

type Entry = {
  /** Set when its awaiter gave up waiting; the entry is then skipped. */
  abandoned: boolean;
  fail: (error: unknown) => void;
  operation: string;
  start: () => void;
};

/**
 * A FIFO serial queue with no unbounded wait in it: every task has a deadline to START (its wait in
 * line) and a deadline to FINISH, and an overrun either advances the line or poisons the queue —
 * the head of the line can never park everyone behind it forever. Use it for every mutex, lock and
 * promise-chain queue instead of `tail = tail.then(...)`.
 */
export function createBoundedSerialQueue({
  name,
  onOverrun,
  onPoisoned,
  runDeadline,
  waitDeadline,
}: {
  name: string;
  onOverrun: SerialQueueOverrunPolicy;
  /** Called once when the queue is poisoned, so the owner can replace the guarded resource. */
  onPoisoned?: (cause: unknown) => void;
  runDeadline: Deadline;
  waitDeadline: Deadline;
}): BoundedSerialQueue {
  const waiting: Entry[] = [];
  let running = false;
  let poisonedBy: { cause: unknown } | null = null;

  const poison = (cause: unknown): void => {
    if (poisonedBy !== null) {
      return;
    }
    poisonedBy = { cause };
    const error = new SerialQueuePoisonedError(name, cause);
    for (const entry of waiting.splice(0)) {
      entry.fail(error);
    }
    try {
      onPoisoned?.(cause);
    } catch {
      // The owner's recovery failing cannot un-poison the queue.
    }
  };

  const startNext = (): void => {
    if (running || poisonedBy !== null) {
      return;
    }
    let entry = waiting.shift();
    while (entry?.abandoned === true) {
      entry = waiting.shift();
    }
    if (entry === undefined) {
      return;
    }
    running = true;
    entry.start();
  };

  const run = <T>(
    operation: string,
    task: (signal: AbortSignal) => Promise<T>,
    options?: { runDeadline?: Deadline; waitDeadline?: Deadline },
  ): Promise<T> => {
    if (poisonedBy !== null) {
      return Promise.reject(new SerialQueuePoisonedError(name, poisonedBy.cause));
    }
    const label = `${name}: ${operation}`;
    const runDeadlineMs = resolveDeadlineMs(label, options?.runDeadline ?? runDeadline);

    // Waiting in line is itself a bounded wait: the hold resolves when it is our turn.
    let entry!: Entry;
    const turn = boundedPromise<void>(
      { deadline: options?.waitDeadline ?? waitDeadline, operation: `${label} (waiting in line)` },
      (resolve, reject) => {
        entry = { abandoned: false, fail: reject, operation, start: resolve };
        waiting.push(entry);
        startNext();
        return () => {
          // Settled by any route: if it timed out while still in line, skip it when its turn comes.
          entry.abandoned = true;
        };
      },
    );

    return turn.then(() => {
      let finished = false;
      const finish = (): void => {
        if (finished) {
          return;
        }
        finished = true;
        running = false;
        startNext();
      };
      const controller = new AbortController();
      const work = task(controller.signal);
      // The queue advances when the task truly settles...
      void work.then(finish, finish);
      if (runDeadlineMs === null) {
        return work;
      }
      // ...or, at the run deadline, according to the overrun policy.
      return boundedPromise<T>({ deadline: runDeadlineMs, operation: label }, (resolve, reject) => {
        work.then(resolve, reject);
      }).catch((error: unknown) => {
        if (!finished && error instanceof DeadlineExceededError) {
          controller.abort(error);
          if (onOverrun === "poison") {
            poison(error);
          } else {
            finish();
          }
        }
        throw error;
      });
    });
  };

  return {
    get idle() {
      return !running && waiting.every((entry) => entry.abandoned);
    },
    poison,
    get poisoned() {
      return poisonedBy !== null;
    },
    run,
  };
}
